import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { getIntersectionRoot } from '@theme/scroll-container';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * The section file rendered through the Section Rendering API to build the cards. Rendering a
 * section by file name gives it this id, which `sections/recently-viewed.liquid` uses to output
 * only the ordered card list.
 */
const RESULTS_SECTION_ID = 'recently-viewed';

/** Fallback when the markup doesn't provide a product limit. */
const DEFAULT_MAX_PRODUCTS = 8;

/**
 * Shows the products the shopper recently viewed on this device.
 *
 * The section renders hidden. With stored history, placeholders reserve the cards' space right away;
 * when it nears the viewport, the stored ids (minus the product being viewed) are rendered as cards
 * with a search request, the same way the search modal renders its recently viewed products. The
 * section stays hidden when there is nothing to show; in the theme editor a note is shown instead so
 * the section can still be found and edited.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} content - Wrapper around the section that stays hidden until there is something to show.
 * @property {HTMLElement} list - The list that receives the product cards.
 * @property {HTMLElement} [editorNote] - Theme editor note shown when there is nothing to show.
 *
 * @extends {Component<Refs>}
 */
class RecentlyViewedComponent extends Component {
  requiredRefs = ['content', 'list'];

  /**
   * Cancels listeners for the current connection.
   * @type {AbortController | undefined}
   */
  #connection;

  /**
   * Cancels the request in flight.
   * @type {AbortController | undefined}
   */
  #request;

  /**
   * Starts loading when the section nears the viewport.
   * @type {IntersectionObserver | undefined}
   */
  #observer;

  /**
   * The ids the list currently shows, comma separated; `null` before the first render.
   * @type {string | null}
   */
  #renderedKey = null;

  /**
   * A combined-listing product swap morphs <main>, which rewrites data-exclude-id, re-hides the
   * content wrapper and empties the list, so the section loads again for the new product.
   * (morph's updatedCallback hook receives the parsed node, so it never runs for this element.)
   * @type {MutationObserver}
   */
  #excludeObserver = new MutationObserver(() => this.#reload());

  connectedCallback() {
    super.connectedCallback();

    this.#connection?.abort();
    this.#connection = new AbortController();
    const { signal } = this.#connection;

    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    document.addEventListener(RecentlyViewed.CLEARED_EVENT, this.#handleCleared, { signal });
    this.#excludeObserver.observe(this, { attributes: true, attributeFilter: ['data-exclude-id'] });

    // A morph can also move this element after updating it, which reconnects it and drops the
    // pending attribute record, so every connection loads from a reset state.
    this.#reload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#excludeObserver.disconnect();
    this.#connection?.abort();
    this.#connection = undefined;
    this.#request?.abort();
    this.#request = undefined;
    this.#disconnectObserver();
  }

  /**
   * The ids to render: the stored history without the product being viewed.
   * @returns {string[]}
   */
  get #productIds() {
    const excludeId = this.dataset.excludeId ?? '';

    try {
      return RecentlyViewed.getProducts().filter((id) => id !== excludeId);
    } catch {
      return [];
    }
  }

  /**
   * The maximum number of cards to show.
   * @returns {number}
   */
  get #maxProducts() {
    const max = Number.parseInt(this.dataset.maxProducts ?? '', 10);
    return Number.isNaN(max) || max < 1 ? DEFAULT_MAX_PRODUCTS : max;
  }

  /**
   * Waits for the section to near the viewport, then loads it.
   * Without history there is nothing to request, so the empty state is applied right away.
   */
  #observe() {
    this.#disconnectObserver();

    const { length } = this.#productIds;

    if (length === 0) {
      this.#renderEmpty();
      return;
    }

    // `null` before the first render, `''` after an empty one; cards already showing stay in place.
    if (!this.#renderedKey) this.#renderSkeleton(Math.min(length, this.#maxProducts));

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#disconnectObserver();
        this.#load();
      },
      { root: getIntersectionRoot(), rootMargin: '0px 0px 400px 0px' }
    );

    this.#observer.observe(this);
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /**
   * Re-checks the history when the page is restored from the back/forward cache, because the
   * shopper has usually just viewed another product.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted || this.#renderedKey === null) return;

    this.#observe();
  };

  /**
   * Re-checks the history after the shopper clears it (for example in the search modal), so the
   * cards already showing, or a request still in flight, don't keep the cleared products on screen.
   */
  #handleCleared = () => {
    this.#reload();
  };

  /**
   * Cancels the request in flight and loads again from the current DOM and history. The rendered
   * key is reset because the list may have been emptied while the stored ids stayed the same.
   */
  #reload() {
    this.#request?.abort();
    this.#request = undefined;
    this.#renderedKey = null;
    this.#observe();
  }

  /**
   * Fetches the cards for the stored ids and renders them.
   */
  async #load() {
    const ids = this.#productIds;
    const key = ids.join(',');

    if (ids.length === 0) {
      this.#renderEmpty();
      return;
    }

    if (key === this.#renderedKey) return;

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    const url = new URL(this.dataset.searchUrl || Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(RESULTS_SECTION_ID, false, url, request.signal);
      if (request.signal.aborted) return;

      this.#render(new DOMParser().parseFromString(html, 'text/html'), key);
    } catch {
      // A failed request leaves nothing to show; aborted requests were replaced or disconnected.
      if (!request.signal.aborted) this.#renderEmpty();
    } finally {
      if (this.#request === request) this.#request = undefined;
    }
  }

  /**
   * Moves the fetched cards into the list, or applies the empty state when none came back.
   * @param {Document} response - The parsed Section Rendering API response.
   * @param {string} key - The ids the response was requested for.
   */
  #render(response, key) {
    const results = response.querySelector('[data-recently-viewed-results]');
    const items = results ? Array.from(results.children).filter((child) => child.tagName === 'LI') : [];

    if (!results || items.length === 0) {
      this.#renderEmpty();
      return;
    }

    // Every stored id is requested so deleted or unpublished products don't leave gaps; keep the most recent.
    const visibleItems = items.slice(0, this.#maxProducts);

    // Size images for this section's columns before they are inserted and start loading.
    const { imageSizes } = this.dataset;
    if (imageSizes) {
      for (const item of visibleItems) {
        for (const image of item.querySelectorAll('img')) image.setAttribute('sizes', imageSizes);
      }
    }

    this.#adoptStylesheet(response);

    const { content, list, editorNote } = this.refs;

    // Fresh nodes (rather than a morph) so a re-render never turns one product's card into another's.
    list.replaceChildren(...visibleItems);
    list.hidden = false;
    if (editorNote) editorNote.hidden = true;
    content.hidden = false;

    this.#renderedKey = key;
  }

  /**
   * Reserves the cards' space while they load, so the content below (usually the footer) doesn't
   * jump when they arrive. `#render()` replaces the placeholders; `#renderEmpty()` collapses them.
   * @param {number} count - The number of cards expected.
   */
  #renderSkeleton(count) {
    const { content, list, editorNote } = this.refs;
    const items = Array.from({ length: count }, () => {
      const item = document.createElement('li');
      item.className = 'recently-viewed__item recently-viewed__skeleton';
      item.setAttribute('aria-hidden', 'true');
      return item;
    });

    list.replaceChildren(...items);
    list.hidden = false;
    if (editorNote) editorNote.hidden = true;
    content.hidden = false;
  }

  /**
   * Hides the section, or shows the theme editor note in its place.
   */
  #renderEmpty() {
    const { content, list, editorNote } = this.refs;

    list.replaceChildren();
    this.#renderedKey = '';

    if (editorNote) {
      list.hidden = true;
      editorNote.hidden = false;
      content.hidden = false;
    } else {
      content.hidden = true;
    }
  }

  /**
   * Card styles ship with any page that renders a resource card on the server (the search modal
   * does on every page). When no card was rendered, the response stylesheet is added to the section
   * so the fetched cards are still styled.
   * @param {Document} response - The parsed Section Rendering API response.
   */
  #adoptStylesheet(response) {
    const stylesheet = response.querySelector('style[data-section-stylesheet]');
    if (!stylesheet || document.querySelector('.resource-card')) return;

    const section = this.closest('.shopify-section') ?? this;
    const existingStylesheet = section.querySelector('style[data-section-stylesheet]');

    if (existingStylesheet) {
      existingStylesheet.textContent = stylesheet.textContent;
    } else {
      section.prepend(stylesheet);
    }
  }
}

if (!customElements.get('recently-viewed-component')) {
  customElements.define('recently-viewed-component', RecentlyViewedComponent);
}
